import React from 'react';
import { Card, CardContent, Grid, Typography } from '@mui/material';
import {
  OBSERVED_SIGNALS_EXPLANATION,
  sanitizeExternalLabel,
} from '../../domain/externalReview/externalReportLanguage';

type Props = {
  evidenceSummary: Record<string, unknown>;
  isDemoOrLoopedSource?: boolean;
};

function formatCount(value: unknown): string {
  if (value == null || value === '') return '—';
  return String(value);
}

export default function ExternalEvidenceSummarySection({ evidenceSummary, isDemoOrLoopedSource }: Props) {
  const rows = [
    {
      label: 'Measured signals',
      detail: 'Raw detections in this scan window. Not 1:1 with reviewable observations.',
      value: formatCount(evidenceSummary.observed_signals),
    },
    {
      label: 'Evidence retained by reference',
      detail: 'Total snapshots/clips retained in signed metadata.',
      value: formatCount(evidenceSummary.evidence_retained_by_reference),
    },
    {
      label: 'Representative evidence shown',
      detail: 'Items visible in this review gallery.',
      value: formatCount(evidenceSummary.representative_evidence_shown),
    },
    {
      label: 'Representative evidence bundled',
      detail: 'Files included in the Signed Safety Scan package.',
      value: formatCount(evidenceSummary.representative_evidence_bundled),
    },
  ].filter((row) => row.value !== '—');

  if (!rows.length) return null;

  return (
    <Card variant="outlined" data-testid="external-evidence-summary">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Evidence summary
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {rows.map((row) => (
            <React.Fragment key={row.label}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {row.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {sanitizeExternalLabel(row.detail, { isDemo: isDemoOrLoopedSource })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="body2">{row.value}</Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
        <Typography variant="body2" color="text.secondary" data-testid="observed-signals-explanation">
          {sanitizeExternalLabel(OBSERVED_SIGNALS_EXPLANATION, { isDemo: isDemoOrLoopedSource })}
        </Typography>
      </CardContent>
    </Card>
  );
}
